// components/MainPage.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import MainTiles from "./MainTiles";
import MainCharts from "./MainCharts";
import MainGrid from "./MainGrid";
import MainFilter from "./MainFilter";
import Tools from "../utils/Tools";
import { useFilter } from "../contexts/FilterContext";

function MainPage() {
  const [rowData, setRowData] = useState([]);
  const { filters, setFilters } = useFilter();
  const [loading, setLoading] = useState(true);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingBaselines, setLoadingBaselines] = useState(false);
  const [loadingBaselinesForTrends, setLoadingBaselinesForTrends] = useState(false);

  const [dateGroupData, setDateGroupData] = useState(null);
  const [dateGroupType, setDateGroupType] = useState(null);
  const [baselines, setBaselines] = useState(null);
  const [intentCounts, setIntentCounts] = useState({});
  const [categories, setCategories] = useState([]);
  const [intents, setIntents] = useState([]);
  const [queues, setQueues] = useState([]);
  const [initiationMethods, setInitiationMethods] = useState([]);
  const [agents, setAgents] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalIntents, setTotalIntents] = useState(0);
  const [totalCategories, setTotalCategories] = useState(0);
  const [topCategories, setTopCategories] = useState([]);
  const [topIntents, setTopIntents] = useState([]);
  const [showTrends, setShowTrends] = useState(false);
  const [sortModel, setSortModel] = useState([]);
  const lastLoadedBaselineFiltersRef = useRef(null);
  const lastLoadedBaselinesRef = useRef(null);

  // Add refs to track request sequences and abort controllers
  const summaryAbortControllerRef = useRef(null);
  const dataAbortControllerRef = useRef(null);
  const baselinesAbortControllerRef = useRef(null);
  const latestSummaryRequestIdRef = useRef(0);
  const latestDataRequestIdRef = useRef(0);
  const latestBaselinesRequestIdRef = useRef(0);

  // Function to build baseline query parameters
  const buildBaselineQueryParams = useCallback(() => {
    const queryParams = new URLSearchParams();

    if (filters.category) {
      queryParams.append("category", filters.category.value);
    }
    if (filters.intent) {
      queryParams.append("intent", filters.intent.value);
    }
    if (filters.agent) {
      let agentValue = filters.agent;
      if (agentValue.value) {
        agentValue = agentValue.value;
      }
      queryParams.append("agent", agentValue);
    }
    if (filters.callId) {
      queryParams.append("callId", filters.callId);
    }
    if (filters.queue && filters.queue.length > 0) {
      filters.queue.forEach((queue) => {
        queryParams.append("queue", queue.value);
      });
    }
    if (filters.initiationMethod && filters.initiationMethod.length > 0) {
      filters.initiationMethod.forEach((initiationMethod) => {
        queryParams.append("initiationMethod", initiationMethod.value);
      });
    }
    if (filters.dateRange) {
      queryParams.append("dateRange", filters.dateRange.value);
    }
    if (filters.dateStart) {
      queryParams.append("dateStart", formatDateToYYYYMMDD(filters.dateStart));
    }
    if (filters.dateEnd) {
      queryParams.append("dateEnd", formatDateToYYYYMMDD(filters.dateEnd));
    }
    if (filters.minorIntents && filters.minorIntents.length > 0) {
      filters.minorIntents.forEach((intent) =>
        queryParams.append("minorIntents", intent.value)
      );
    }
    if (!filters.excludeWeekends) {
      queryParams.append("excludeWeekends", filters.excludeWeekends);
    }

    return queryParams;
  }, [filters]);

  const loadBaselinesIfNeeded = useCallback(async () => {
    // If baselines are already loaded and filters haven't changed, return immediately
    const currentFiltersJSON = JSON.stringify(filters);
    const lastFiltersJSON = lastLoadedBaselineFiltersRef.current;

    if (baselines && lastFiltersJSON === currentFiltersJSON) {
      return Promise.resolve(baselines);
    }

    // If currently loading, return a promise that resolves when loading is complete
    if (loadingBaselines) {
      return new Promise((resolve, reject) => {
        const checkLoading = () => {
          if (!loadingBaselines) {
            if (baselines) {
              resolve(baselines);
            } else {
              reject(new Error("Failed to load baselines"));
            }
          } else {
            setTimeout(checkLoading, 100);
          }
        };
        checkLoading();
      });
    }

    // Check if we have cached data for current filters
    if (lastFiltersJSON === currentFiltersJSON && lastLoadedBaselinesRef.current) {
      console.log("DEBUG - USING CACHED BASELINES (ON DEMAND)");
      setBaselines(lastLoadedBaselinesRef.current);
      return Promise.resolve(lastLoadedBaselinesRef.current);
    }

    // Cancel previous request if it exists
    if (baselinesAbortControllerRef.current) {
      baselinesAbortControllerRef.current.abort();
    }
    
    const abortController = new AbortController();
    baselinesAbortControllerRef.current = abortController;
    const requestId = ++latestBaselinesRequestIdRef.current;
    
    setLoadingBaselines(true);
    const queryParams = buildBaselineQueryParams();

    return Tools.sendRequest("get-baselines", queryParams.toString(), "GET", abortController.signal)
      .then((response) => {
        if (requestId === latestBaselinesRequestIdRef.current) {
          console.log("DEBUG - GOT BASELINES (ON DEMAND)", response);
          setBaselines(response.baselines);
          lastLoadedBaselinesRef.current = response.baselines;
          lastLoadedBaselineFiltersRef.current = currentFiltersJSON;
          setLoadingBaselines(false);
          return response.baselines;
        }
        return null;
      })
      .catch((error) => {
        if (error.name !== 'AbortError' && requestId === latestBaselinesRequestIdRef.current) {
          console.error("Error fetching baselines:", error);
          setBaselines(null);
          lastLoadedBaselinesRef.current = null;
          setLoadingBaselines(false);
          throw error;
        }
        throw error;
      });
  }, [filters, baselines, loadingBaselines, buildBaselineQueryParams]);

  // Fetch summary data from server whenever filters change
  useEffect(() => {
    // Cancel previous request if it exists
    if (summaryAbortControllerRef.current) {
      summaryAbortControllerRef.current.abort();
    }
    
    // Create new abort controller for this request
    const abortController = new AbortController();
    summaryAbortControllerRef.current = abortController;
    
    // Track request sequence
    const requestId = ++latestSummaryRequestIdRef.current;
    
    setLoadingSummary(true);
    const queryParams = new URLSearchParams();

    // Add all filter parameters to queryParams
    if (filters.category) {
      queryParams.append("category", filters.category.value);
    }
    if (filters.intent) {
      queryParams.append("intent", filters.intent.value);
    }
    if (filters.agent) {
      let agentValue = filters.agent;
      if (agentValue.value) {
        agentValue = agentValue.value;
      }
      queryParams.append("agent", agentValue);
    }
    if (filters.callId) {
      queryParams.append("callId", filters.callId);
    }
    if (filters.queue && filters.queue.length > 0) {
      filters.queue.forEach((queue) => {
        queryParams.append("queue", queue.value);
      });
    }
    if (filters.initiationMethod && filters.initiationMethod.length > 0) {
      filters.initiationMethod.forEach((initiationMethod) => {
        queryParams.append("initiationMethod", initiationMethod.value);
      });
    }
    if (filters.dateRange) {
      queryParams.append("dateRange", filters.dateRange.value);
    }
    if (filters.dateStart) {
      queryParams.append("dateStart", formatDateToYYYYMMDD(filters.dateStart));
    }
    if (filters.dateEnd) {
      queryParams.append("dateEnd", formatDateToYYYYMMDD(filters.dateEnd));
    }
    if (filters.minorIntents && filters.minorIntents.length > 0) {
      filters.minorIntents.forEach((intent) =>
        queryParams.append("minorIntents", intent.value)
      );
    }
    if (!filters.excludeWeekends) {
      queryParams.append("excludeWeekends", filters.excludeWeekends);
    }

    Tools.sendRequest("get-summary", queryParams.toString(), "GET", abortController.signal)
      .then((response) => {
        // Only update state if this is still the latest request
        if (requestId === latestSummaryRequestIdRef.current) {
          console.log("DEBUG - GOT SUMMARY", response);

          // Set counts and metadata
          setCategories(
            response.uniqueCategories.map((c) => ({
              value: c,
              label: c,
              count: response.categoryCounts[c],
            }))
          );
          setIntents(
            response.uniqueIntents.map((i) => ({
              value: i,
              label: i,
              count: response.intentCounts[i],
            }))
          );
          setAgents(
            response.uniqueAgents.map((a) => ({
              value: a,
              label: a,
              count: response.agentCounts[a],
            }))
          );
          setQueues(
            Object.keys(response.callsByQueue).map((a) => ({
              value: a,
              label: a,
              count: response.callsByQueue[a],
            }))
          );
          setInitiationMethods(
            Object.keys(response.callsByInitiationMethod).map((a) => ({
              value: a,
              label: a,
              count: response.callsByInitiationMethod[a],
            }))
          );
          setTotalIntents(response.totalIntents);
          setTotalCategories(response.totalCategories);
          setTopCategories(response.topCategories);
          setTopIntents(response.topIntents);
          setDateGroupData(response.dateGroupData);
          setDateGroupType(response.dateGroupType);
          setIntentCounts(response.intentCounts);
          setLoadingSummary(false); // Summary data fetching is complete
        }
      })
      .catch((error) => {
        // Ignore abort errors as they're expected when cancelling
        if (error.name !== 'AbortError' && requestId === latestSummaryRequestIdRef.current) {
          console.error("Error fetching summary:", error);
          setLoadingSummary(false);
        }
      });
    
    return () => {
      if (summaryAbortControllerRef.current) {
        summaryAbortControllerRef.current.abort();
      }
    };
  }, [filters]);

  useEffect(() => {
    if (showTrends) {
      const currentFiltersJSON = JSON.stringify(filters);
      const lastFiltersJSON = lastLoadedBaselineFiltersRef.current;

      if (
        lastFiltersJSON === currentFiltersJSON &&
        lastLoadedBaselinesRef.current
      ) {
        console.log("DEBUG - USING CACHED BASELINES");
        setBaselines(lastLoadedBaselinesRef.current);
      } else {
        if (baselinesAbortControllerRef.current) {
          baselinesAbortControllerRef.current.abort();
        }
        
        const abortController = new AbortController();
        baselinesAbortControllerRef.current = abortController;
        const requestId = ++latestBaselinesRequestIdRef.current;
        
        // Use separate loading state for trends
        setLoadingBaselinesForTrends(true);
        const queryParams = buildBaselineQueryParams();

        Tools.sendRequest("get-baselines", queryParams.toString(), "GET", abortController.signal)
          .then((response) => {
            if (requestId === latestBaselinesRequestIdRef.current) {
              console.log("DEBUG - GOT BASELINES", response);
              setBaselines(response.baselines);
              lastLoadedBaselinesRef.current = response.baselines;
              lastLoadedBaselineFiltersRef.current = currentFiltersJSON;
              setLoadingBaselinesForTrends(false);  // Use separate state
            }
          })
          .catch((error) => {
            if (error.name !== 'AbortError' && requestId === latestBaselinesRequestIdRef.current) {
              console.error("Error fetching baselines:", error);
              setBaselines(null);
              lastLoadedBaselinesRef.current = null;
              setLoadingBaselinesForTrends(false);  // Use separate state
            }
          });
      }
    } else {
      setBaselines(null);
    }
  }, [filters, showTrends, buildBaselineQueryParams]);

  // Fetch paged data from server whenever page changes
  useEffect(() => {
    // Cancel previous request if it exists
    if (dataAbortControllerRef.current) {
      dataAbortControllerRef.current.abort();
    }
    
    // Create new abort controller for this request
    const abortController = new AbortController();
    dataAbortControllerRef.current = abortController;
    
    // Track request sequence
    const requestId = ++latestDataRequestIdRef.current;
    
    setLoading(true);
    const queryParams = new URLSearchParams();
    if (filters.category) {
      queryParams.append("category", filters.category.value);
    }
    if (filters.intent) {
      queryParams.append("intent", filters.intent.value);
    }
    if (filters.agent) {
      let agentValue = filters.agent;
      if (agentValue.value) {
        agentValue = agentValue.value;
      }
      queryParams.append("agent", agentValue);
    }
    if (filters.callId) {
      queryParams.append("callId", filters.callId);
    }
    if (filters.dateRange) {
      queryParams.append("dateRange", filters.dateRange.value);
    }
    if (filters.dateStart) {
      queryParams.append("dateStart", formatDateToYYYYMMDD(filters.dateStart));
    }
    if (filters.dateEnd) {
      queryParams.append("dateEnd", formatDateToYYYYMMDD(filters.dateEnd));
    }
    if (filters.minorIntents) {
      if (Array.isArray(filters.minorIntents)) {
        filters.minorIntents.forEach((intent) =>
          queryParams.append("minorIntents", intent.value)
        );
      } else {
        queryParams.append("minorIntents", filters.minorIntents);
      }
    }
    if (filters.queue && filters.queue.length > 0) {
      filters.queue.forEach((queue) => {
        queryParams.append("queue", queue.value);
      });
    }
    if (filters.initiationMethod && filters.initiationMethod.length > 0) {
      filters.initiationMethod.forEach((initiationMethod) => {
        queryParams.append("initiationMethod", initiationMethod.value);
      });
    }
    if (!filters.excludeWeekends) {
      queryParams.append("excludeWeekends", filters.excludeWeekends);
    }
    queryParams.append("page", currentPage);

    // sorting parameters
    if (sortModel && sortModel.length > 0) {
      sortModel.forEach((sortItem) => {
        queryParams.append("sortBy", sortItem.colId);
        queryParams.append("sortOrder", sortItem.sort);
      });
    }

    Tools.sendRequest("get-data", queryParams.toString(), "GET", abortController.signal)
      .then((response) => {
        // Only update state if this is still the latest request
        if (requestId === latestDataRequestIdRef.current) {
          console.log("DEBUG - GOT DATA", response);
          setRowData(response.data);
          setTotalRecords(response.totalRecords);
          setTotalPages(response.totalPages);
          let receivedPage = +response.currentPage;
          if (
            response.currentPage === null ||
            isNaN(receivedPage) ||
            receivedPage === 0
          ) {
            console.warn(
              `Invalid currentPage received (${response.currentPage}). Setting to 1.`
            );
            setCurrentPage(1);
          } else {
            setCurrentPage(receivedPage);
          }
          setLoading(false); // Data fetching is complete
        }
      })
      .catch((error) => {
        // Ignore abort errors as they're expected when cancelling
        if (error.name !== 'AbortError' && requestId === latestDataRequestIdRef.current) {
          console.error("Error fetching data:", error);
          setLoading(false);
        }
      });
    
    return () => {
      if (dataAbortControllerRef.current) {
        dataAbortControllerRef.current.abort();
      }
    };
  }, [currentPage, filters, sortModel]);

  const handleFilterChange = useCallback(
    (newFilters) => {
      setFilters(newFilters);
      setCurrentPage(1);
      // Turn off trends when filters change
      setShowTrends(false);
    },
    [setFilters]
  );

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  const handleSortChange = useCallback((newSortModel) => {
    setSortModel(newSortModel);
  }, []);

  const handleShowTrends = useCallback((showTrends) => {
    setShowTrends(showTrends);
  }, []);

  return (
    <div style={{ 'max-width': '1750px'
}} className="container mx-auto p-4">
      <MainFilter
        setFilters={handleFilterChange}
        updateShowTrends={handleShowTrends}
        categories={categories}
        intents={intents}
        agents={agents}
        initiationMethods={initiationMethods}
        queues={queues}
        showTrends={showTrends} 
      />
      {loading || loadingSummary || loadingBaselinesForTrends ? (
        <div className="flex justify-center items-center h-64">
          <div className="loader"></div>
        </div>
      ) : (
        <>
          <MainTiles
            callCount={totalRecords}
            agentCount={agents.length}
            totalIntents={totalIntents}
            totalCategories={totalCategories}
            baselines={baselines}
            showTrends={showTrends}
            dateRange={filters.dateRange.value}
          />
          <MainCharts
            topCategories={topCategories}
            topIntents={topIntents}
            baselines={baselines}
            loadingBaselines={loadingBaselines}
            loadBaselinesIfNeeded={loadBaselinesIfNeeded}
            intentCounts={intentCounts}
            dateGroupData={dateGroupData}
            dateGroupType={dateGroupType}
            showTrends={showTrends}
            dateRange={filters.dateRange.value}
          />
          <MainGrid
            data={rowData}
            currentPage={currentPage}
            totalPages={totalPages}
            totalRecs={totalRecords}
            onPageChange={handlePageChange}
            onSortChange={handleSortChange}
            filters={filters}
            sortModel={sortModel}
          />
        </>
      )}
    </div>
  );
}

export default MainPage;

function formatDateToYYYYMMDD(date) {
  const d = new Date(date);
  let month = "" + (d.getMonth() + 1);
  let day = "" + d.getDate();
  const year = d.getFullYear();

  if (month.length < 2) month = "0" + month;
  if (day.length < 2) day = "0" + day;

  return [year, month, day].join("-");
}